import * as React from 'react';
import { withRouter } from 'react-router-dom';

import classNames from 'classnames';
import { reaction } from 'mobx';
import { inject, observer } from 'mobx-react';

import { getConfig, URLS } from '@constants';

import { EAppPicSize, EAppsCounters, EAppsGenreEng, EContext, ERefsApps, EResponseStatus, EStore } from '@enums';

import { counters, getAppSchema, translations } from '@utils';

import { commonWillUnmount, fetchData, Translate } from '@decorators';

import { IAppsByGenreStore, IAppsStore, IAppState, IUiStoreParams } from '@stores';

import { Api } from '@api';

import {
	AppDescription,
	AppHeader,
	AppRatingBar,
	AppsCarousel,
	Breadcrumbs,
	ContentPanel,
	ContentWrapper,
	EAppItemType,
	ErrorOfflineScreen,
	ErrorScreen,
	JsonLdSchema,
	Pic,
	PreloaderScreen,
	ScreenshotCarousel,
} from '@components';

import { OuterWrapper } from '@ui/OuterWrapper';

import { IAppData } from '@interfaces';

import { IAppSeoPageProps, IMatchAppSeoPageParams } from './types';

import * as s from './AppSeoPage.scss';

const fetchDataArgs = {
	context: EContext.MOBILE,
};

async function fetchAndConfigure(
	appId: string,
	appsByIdStore: IAppsStore,
	uiStore: IUiStoreParams,
	appsByGenreCompilationStore: IAppsByGenreStore,
) {
	await appsByIdStore.fetch(fetchDataArgs, [appId]);
	const { id, genres, title: pageTitle } = appsByIdStore?.data?.apps?.[0] || {};

	if (id) {
		const firstGenre = genres.length ? genres[0]?.id as EAppsGenreEng : null;

		if (uiStore.status !== EResponseStatus.OK) {
			uiStore.setTopMenuInitialState({
				backButtonUrl: true,
				pageTitle,
			});
		}

		if (firstGenre && appsByGenreCompilationStore?.getByGenre(firstGenre)?.status !== EResponseStatus.OK) {
			await appsByGenreCompilationStore.fetch(firstGenre, { ...fetchDataArgs, limit: 20 });
		}
	}
}

const POPULAR_APPS_FETCH_CONFIGS = {
	context: EContext.MOBILE,
	limit: 10,
};

@inject(
	EStore.APPS_BY_ID,
	EStore.APPS_POPULAR,
	EStore.APPS_BY_GENRE_COMPILATION,
	EStore.UI,
	EStore.MAIN_MENU_APPS,
	EStore.COUNTERS,
	EStore.CURRENT_USER,
)
@observer
@Translate('pages.AppSeoPage')
@commonWillUnmount()
@fetchData([
	// App by id + UI STATE
	async ({ appsByIdStore, uiStore, appsByGenreCompilationStore }: IAppState, { appId }: IMatchAppSeoPageParams) => {
		uiStore.topMenuBurgerButton.toggleBackMode(true);
		if (!appsByIdStore.data?.apps?.length && appsByIdStore.status !== EResponseStatus.OK && appId) {
			await fetchAndConfigure(appId, appsByIdStore, uiStore, appsByGenreCompilationStore);
		}
	},

	// Popular apps
	async ({ appsPopularStore }: IAppState) => {
		if (!appsPopularStore.data?.apps?.length && appsPopularStore.status !== EResponseStatus.OK) {
			await appsPopularStore?.fetch(POPULAR_APPS_FETCH_CONFIGS);
		}
	},
])
export class AppSeoPageContainer extends React.Component<IAppSeoPageProps, { isCoverLoaded: boolean }> {

	constructor(props: IAppSeoPageProps) {
		super(props);

		props.uiStore.topMenuBurgerButton.toggleBackMode(true);
	}

	state = {
		isCoverLoaded: false,
	};

	componentDidMount() {
		this.sortGenreCompilation();

		reaction(
			() => this.props.uiStore.isOnline,
			// eslint-disable-next-line @typescript-eslint/no-misused-promises
			async (isOnline) => {
				if (isOnline && !this.props.appsByIdStore?.data?.apps?.length) {
					await this.updateData();
					await this.props.appsPopularStore.fetch(POPULAR_APPS_FETCH_CONFIGS);
				}
			},
		);
	}

	componentWillUnmount(): void {
		if (!IS_SSR) {
			this.props.appsByIdStore.clearStore();
			this.props.appsPopularStore.clearStore();
		}
	}

	async componentDidUpdate(prevProps: Readonly<IAppSeoPageProps>) {
		const {
			uiStore,
			match,
		} = this.props;
		const appId = match?.params?.appId;

		if (appId !== prevProps.match?.params?.appId || uiStore.isOnline !== prevProps.uiStore.isOnline) {
			this.setState({
				isCoverLoaded: false,
			});
			await this.updateData();
		}
	}

	async updateData() {
		const {
			appsByIdStore,
			appsByGenreCompilationStore,
			uiStore,
			match,
		} = this.props;
		const appId = match?.params?.appId;

		await fetchAndConfigure(appId, appsByIdStore, uiStore, appsByGenreCompilationStore);
		this.sortGenreCompilation();

		this.props.uiStore.pageTitle.setTitle(this.props.appsByIdStore?.data?.apps[0]?.title);
	}

	sortGenreCompilation() {
		const {
			appsPopularStore,
			appsByGenreCompilationStore,
			appsByIdStore: { data: appsByIdData } = {},
		} = this.props;
		const genre = appsByIdData?.apps?.[0]?.genres?.[0]?.id || null;
		const idsListForSort = appsPopularStore?.data?.apps?.map((app) => app.id) || [];

		if (idsListForSort?.length && genre) {
			appsByGenreCompilationStore.sortAppsByUniqueFirst(genre as EAppsGenreEng, idsListForSort);
		}
	}

	onVote = (results: { chosenRating: number }) => {
		const {
			chosenRating,
		} = results;
		const {
			appsByIdStore,
			appsByIdStore: { data: { apps } },
		} = this.props;
		const app = apps[0];

		appsByIdStore.setRating(app.id, chosenRating);
	};

	onClickPopularAppsPageLink = () => {
		counters.sendRB(EAppsCounters.SHOW_MORE_POPULAR);
		this.props.appsPopularStore.clearStore();
	};

	onLaunchButtonClick = () => {
		const currentPageHref = window.location.href;

		if (!this.props.currentUserStore?.currentUser?.id) {
			Api.currentUser.logIn(currentPageHref);
		} else {
			window.location.href = window.location.href.replace(/((&|\?)promo=[^(#?&)]*)/g, '');
		}
	};

	onCoverLoad = () => {
		this.setState({
			isCoverLoaded: true,
		});
	};

	render() {
		const {
			appsPopularStore,
			appsByGenreCompilationStore,
			appsByIdStore: { data = null, status: appsStatus } = {},
			uiStore,
			currentUserStore,
		} = this.props;
		const apps = data?.apps;
		const appsCatalogBasePath = getConfig().apps.pathname || URLS.APPS;
		const app = (apps?.length && apps[0]) || null;
		const { title, rating, voters, installs, description, id, genres = [], screenshots = [] } = app || {};
		const genre = genres[0]?.id as EAppsGenreEng;
		const appScreenshots = screenshots?.map((screenshot: string) => {
			return {
				pic: screenshot,
				alt: `${this.t('gameProcess')} ${title}`,
			};
		});
		const compilation = genre ? appsByGenreCompilationStore.getByGenre(genre) : null;
		const isFake = appsStatus === EResponseStatus.PENDING;
		const picCover = !isFake ? app?.pics[EAppPicSize.PIC_980_370] : '';

		if (!app) {
			if (appsStatus === EResponseStatus.ERROR) {
				return uiStore.isOnline
					? <ErrorScreen>{this.t('error')}</ErrorScreen>
					: <ErrorOfflineScreen/>;
			} else {
				return <PreloaderScreen/>;
			}
		}

		return (
			<OuterWrapper className={classNames(s.block, { [s.blockFake]: isFake })}>
				{ id &&
					(
						<JsonLdSchema
							id={id}
							schema={
								getAppSchema({
									id,
									title,
									description,
									genres,
									rating,
									voters,
									image: picCover,
								})
							}
						/>
					)
				}

				<div className={s.appCover}>
					<Pic src={picCover}
						onSuccessLoad={this.onCoverLoad}
						className={classNames(s.appCoverImage, { [s.appCoverImageLoaded]: this.state.isCoverLoaded })}
						alt={`${this.t('game')} ${title}`}
					/>
				</div>

				<ContentWrapper className={s.section}>
					<AppHeader
						isFake={isFake}
						className={s.header}
						appId={id}
						title={title}
						rating={rating}
						voters={voters}
						installs={installs}
						isMyCreated={currentUserStore.isMyCreated}
						onLaunchButtonClick={this.onLaunchButtonClick}
					/>

					<ScreenshotCarousel
						isFake={isFake}
						className={classNames(s.screenshots, s.carousel)}
						screenshots={appScreenshots}
					/>

					<AppDescription
						isFake={isFake}
						className={s.description}
						description={description}
					/>

					{!isFake && <AppRatingBar
						className={s.rating}
						// votesCount={voters}
						appId={id}
						callback={this.onVote}
					/>
					}
				</ContentWrapper>

				{ !isFake && <ContentWrapper className={s.section}>

					{/* популярные игры */}
					<ContentPanel
						title={translations.appCatalog('appsPopular')}
						linkUrl={URLS.CATALOG_POPULAR}
						onLinkClick={this.onClickPopularAppsPageLink}
					>
						<AppsCarousel
							className={s.carousel}
							// исключаем открытую игру из списка
							apps={appsPopularStore.data?.apps?.filter((appItem: IAppData) => appItem.id !== app.id) || []}
							responseStatus={appsPopularStore.status}
							type={EAppItemType.MEDIUM}
							urlRef={ERefsApps.LINE_POPULAR}
							hasTitle
							hasInstalls
						/>
					</ContentPanel>

					{/* Игры схожего жанра */}
					{genre &&
					<ContentPanel
						className={s.contentPanel}
						title={translations.appCatalog(genre)}
						// todo: добавить счётчик
						linkUrl={`/${genre}`}
					>
						<AppsCarousel
							className={s.carousel}
							// исключаем открытую игру из списка
							apps={compilation && compilation?.data.filter((appItem: IAppData) => appItem.id !== app.id) || []}
							responseStatus={compilation?.status || EResponseStatus.PENDING}
							type={EAppItemType.MEDIUM}
							urlRef={ERefsApps.LINE_NEW}
							hasTitle
							hasInstalls
						/>
					</ContentPanel>
					}

					<Breadcrumbs
						className={s.breadcrumbs}
						breadcrumbs={[
							{
								title: translations.appCatalog('apps-all'),
								link: appsCatalogBasePath,
							},
							{
								link: `${appsCatalogBasePath}/${genre}`,
								title: translations.appCatalog(genre),
							},
							{
								title: app.title,
							},
						]}
					/>
				</ContentWrapper>
				}
			</OuterWrapper>
		);
	}

}

export const AppSeoPage = withRouter(AppSeoPageContainer);

